/**
 * SCHADS MA000100 PR798459 (Gibian VP, Dobson DP, Perica C, 13 April 2026).
 *
 * Operative from the employee's first full pay period starting on or after
 * 1 June 2026. Callers without pay-period context date-gate on the tenant-local
 * shift date — slightly early for a period that straddles 1 June, and never
 * applied to pre-1 June dates (pre-June is a separate back-pay question).
 *
 * Artifact:
 * docs/operations/clause-verifications/2026-08-15-aged-care-legislation-schads-pr798459-sleepover.md
 */
import { DEFAULT_TZ, dateOnlyInTz } from './timezone';
import type { ShiftSegment } from './award-engines/shift-classifier';

/** Instrument commencement. Not the same as "apply to historical rows". */
export const PR798459_OPERATIVE_ON = '2026-06-01';

/** cl 25.1(c) / 28.1(b)(iii) ordinary-hours ceiling for an agreed wrap. */
export const PR798459_TWELVE_HOUR_ORDINARY_CAP = 12;

/** cl 25.1(c) / 28.1(b)(iv) — max 8 ordinary hours either side of the sleepover. */
export const PR798459_MAX_ORDINARY_EITHER_SIDE = 8;

export type SleepoverWindow = {
  start: Date;
  end: Date;
};

export type SleepoverPortion = 'before' | 'after' | 'undivided';

export function isPr798459OperativeOnDate(ymd: string): boolean {
  return ymd >= PR798459_OPERATIVE_ON;
}

export function isPr798459Operative(shiftStart: Date, timezone: string = DEFAULT_TZ): boolean {
  return isPr798459OperativeOnDate(dateOnlyInTz(shiftStart, timezone));
}

/**
 * cl 25.4(c): work immediately before and immediately after a sleepover is the
 * same shift, so the 10-hour rest in cl 25.4(a) is not measured between those
 * two periods. Both periods must be marked — a lone sleepover flag on one of
 * two adjacent distinct shifts must not suppress a real rest breach.
 */
export function isSleepoverFlankSameShift(args: {
  prevHasSleepover: boolean;
  currHasSleepover: boolean;
  laterShiftDateYmd: string;
  /** Hours between prev end and curr start. Wrap flanks abut (~0). */
  gapHours?: number;
}): boolean {
  if (!isPr798459OperativeOnDate(args.laterShiftDateYmd)) return false;
  // Legacy two-row wrap: both flanks marked, sleepover is the gap.
  if (args.prevHasSleepover && args.currHasSleepover) return true;
  // Production three-row wrap: only the middle row is marked; flanks abut.
  if (!(args.prevHasSleepover || args.currHasSleepover)) return false;
  if (typeof args.gapHours === 'number' && args.gapHours > 0.25) return false;
  return true;
}

export function resolvedSleepoverWindow(
  hasSleepover: boolean | undefined,
  start: Date | undefined,
  end: Date | undefined,
  shiftStart: Date,
  timezone: string = DEFAULT_TZ,
): SleepoverWindow | null {
  if (!hasSleepover || !start || !end) return null;
  if (!(end.getTime() > start.getTime())) return null;
  if (!isPr798459Operative(shiftStart, timezone)) return null;
  return { start, end };
}

/**
 * SCHADS PER_SEGMENT chunks are 15 minutes from shift start (including the
 * documented over-count on non-aligned ends). Reconstruct each chunk start
 * the same way so a sleepover window can drop those hours from loadings.
 */
export function annotateSchadsSegments(
  shiftStart: Date,
  segments: readonly ShiftSegment[],
): Array<ShiftSegment & { segmentStart: Date; portion: SleepoverPortion }> {
  const chunkMs = 15 * 60 * 1000;
  return segments.map((seg, index) => ({
    ...seg,
    segmentStart: new Date(shiftStart.getTime() + index * chunkMs),
    portion: 'undivided' as const,
  }));
}

export function portionForSegmentStart(
  segmentStart: Date,
  window: SleepoverWindow | null,
): SleepoverPortion {
  if (!window) return 'undivided';
  if (segmentStart.getTime() < window.start.getTime()) return 'before';
  return 'after';
}

export function segmentOverlapsSleepover(
  segmentStart: Date,
  segmentHours: number,
  window: SleepoverWindow,
): boolean {
  const segmentEndMs = segmentStart.getTime() + segmentHours * 60 * 60 * 1000;
  return segmentStart.getTime() < window.end.getTime() && segmentEndMs > window.start.getTime();
}

export function workSegmentsOutsideSleepover(
  shiftStart: Date,
  segments: readonly ShiftSegment[],
  window: SleepoverWindow | null,
): Array<ShiftSegment & { segmentStart: Date; portion: SleepoverPortion }> {
  const annotated = annotateSchadsSegments(shiftStart, segments);
  if (!window) return annotated;
  return annotated
    .filter((seg) => !segmentOverlapsSleepover(seg.segmentStart, seg.hours, window))
    .map((seg) => ({
      ...seg,
      portion: portionForSegmentStart(seg.segmentStart, window),
    }));
}
